import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {catchError, finalize, map, shareReplay, tap} from 'rxjs/operators';
import {ApiService, FeatureFlag} from '../../services/api.service';

export type FeatureFlagStatus = 'DISABLED' | 'ENABLED' | 'COMING_SOON';

export interface FeatureFlagState {
  key: string;
  status: FeatureFlagStatus;
  visible: boolean;
  accessible: boolean;
}

const FEATURE_FLAG_DEFAULTS: Record<string, FeatureFlagState> = {
  'dashboard.commands': { key: 'dashboard.commands', status: 'ENABLED', visible: true, accessible: true },
  'dashboard.auto_messages': { key: 'dashboard.auto_messages', status: 'ENABLED', visible: true, accessible: true },
  'dashboard.moderation': { key: 'dashboard.moderation', status: 'ENABLED', visible: true, accessible: true },
  'dashboard.planning': { key: 'dashboard.planning', status: 'ENABLED', visible: true, accessible: true },
	'dashboard.tools': {key: 'dashboard.tools', status: 'ENABLED', visible: true, accessible: true},
  'dashboard.tools.levels': { key: 'dashboard.tools.levels', status: 'ENABLED', visible: true, accessible: true },
  'dashboard.tools.followers': { key: 'dashboard.tools.followers', status: 'ENABLED', visible: true, accessible: true },
  'dashboard.tools.panel_streamer': { key: 'dashboard.tools.panel_streamer', status: 'ENABLED', visible: true, accessible: true },
  'dashboard.spotify': { key: 'dashboard.spotify', status: 'ENABLED', visible: true, accessible: true },
  'dashboard.discord': { key: 'dashboard.discord', status: 'ENABLED', visible: true, accessible: true },
  'dashboard.youtube': { key: 'dashboard.youtube', status: 'COMING_SOON', visible: true, accessible: false },
  'dashboard.overlays': { key: 'dashboard.overlays', status: 'COMING_SOON', visible: true, accessible: false },
  'dashboard.bot_imports': { key: 'dashboard.bot_imports', status: 'DISABLED', visible: false, accessible: false }
};

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagsService {
  private readonly states$ = new BehaviorSubject<Record<string, FeatureFlagState>>({...FEATURE_FLAG_DEFAULTS});
  private loading$?: Observable<Record<string, FeatureFlagState>>;
  private loaded = false;

  constructor(private readonly api: ApiService) {}

  ensureLoaded(force = false): Observable<Record<string, FeatureFlagState>> {
    if (force) {
      this.loaded = false;
      this.loading$ = undefined;
    }

    if (this.loaded) {
      return of(this.states$.getValue());
    }

    if (this.loading$) {
      return this.loading$;
    }

    this.loading$ = this.api.getFeatureFlags().pipe(
      map((flags) => this.mergeStatuses(flags)),
      catchError(() => of({...FEATURE_FLAG_DEFAULTS})),
      tap((states) => {
        this.states$.next(states);
        this.loaded = true;
      }),
      finalize(() => {
        this.loading$ = undefined;
      }),
      shareReplay(1)
    );

    return this.loading$;
  }

  getState(featureKey: string): FeatureFlagState {
    const normalizedKey = (featureKey ?? '').trim();
    return this.states$.getValue()[normalizedKey] ?? FEATURE_FLAG_DEFAULTS[normalizedKey] ?? {
      key: normalizedKey,
      status: 'ENABLED',
      visible: true,
      accessible: true
    };
  }

  getStatus(featureKey: string): FeatureFlagStatus {
    return this.getState(featureKey).status;
  }

  isEnabled(featureKey: string): boolean {
    return this.getStatus(featureKey) === 'ENABLED';
  }

  isComingSoon(featureKey: string): boolean {
    return this.getStatus(featureKey) === 'COMING_SOON';
  }

  isVisible(featureKey: string): boolean {
    return this.getState(featureKey).visible;
  }

  isAccessible(featureKey: string): boolean {
    return this.getState(featureKey).accessible;
  }

  private mergeStatuses(flags: FeatureFlag[] | null | undefined): Record<string, FeatureFlagState> {
    const nextStatuses: Record<string, FeatureFlagState> = {...FEATURE_FLAG_DEFAULTS};
    for (const flag of flags ?? []) {
      if (!flag?.key || !flag?.status) {
        continue;
      }
      nextStatuses[flag.key] = {
        key: flag.key,
        status: flag.status,
        visible: flag.visible ?? flag.status !== 'DISABLED',
        accessible: flag.accessible ?? flag.status === 'ENABLED'
      };
    }
    return nextStatuses;
  }
}
